<template>
  <teleport to="#app">
    <transition name="fade">
      <div
        id="popup-container"
        v-if="show"
      >
        <div
          id="popup-wrapper"
          :class="[
            { flex: isFlex, mobile: checkOnDevice()[0] || checkOnDevice()[2] },
            variant === 'default' ? 'p-5' : 'pr-5 pl-5 pt-5',
          ]"
          :style="{
            width: width,
            maxHeight: variant === 'default' ? '50%' : '95%',
            '--popup-min-h': variant === 'default' ? '600px' : '90%',
          }"
        >
          <div
            id="close"
            @click="close"
          >
            <rc-svg name="croix"></rc-svg>
          </div>
          <div
            :class="[
              'p-0 flex-1 flex flex-col h-full-minor-twenty',
              { 'mt-5': variant === 'default' },
            ]"
          >
            <slot name="header">
              <div id="popup-header">
                <div
                  id="popup-icon"
                  v-if="iconName"
                  :class="[{ large: iconLarge }, { 'no-stroke': !iconStroke }]"
                >
                  <rc-svg :name="iconName"></rc-svg>
                </div>
                <div
                  id="title"
                  v-if="titleText"
                >
                  {{ titleText }}
                </div>
              </div>
            </slot>
            <div
              class="flex flex-col"
              id="content"
            >
              <slot name="component">
                <component :is="usedComponent"></component>
              </slot>
              <slot name="desc">
                <div
                  class="description"
                  v-if="description"
                  :style="{ 'max-width': maxWidthDesc }"
                  v-html="description"
                ></div>
              </slot>
            </div>
            <slot
              name="footer"
              v-if="variant === 'default'"
            >
              <div
                id="footer"
                :class="{ 'centered-btn': centeredFooter }"
              >
                <RdbkButton
                  id="close-btn"
                  :mode="'important'"
                  @is-clicked="close"
                  :label="footerButtonText"
                ></RdbkButton>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import RcSvg from '@/components/medias/RcSvg.vue';
  import RdbkButton from '@/components/inputs/RdbkButton.vue';
  import mixin from '@/utils/mixins/RcMixin.vue';

  export default defineComponent({
    components: {
      RcSvg,
      RdbkButton,
    },
    inheritAttrs: false,
    props: {
      width: {
        type: String,
        default: '',
      },
      show: {
        type: Boolean,
        required: true,
      },
      description: {
        type: String,
        default: '',
      },
      maxWidthDesc: {
        type: String,
        default: '',
      },
      iconName: {
        type: String,
        default: '',
      },
      titleText: {
        type: String,
        default: '',
      },
      usedComponent: {
        type: String,
        default: '',
      },
      centeredFooter: {
        type: Boolean,
        default: true,
      },
      footerButtonText: {
        type: String,
        default: 'OK',
      },
      isFlex: {
        type: Boolean,
        default: false,
      },
      iconStroke: {
        type: Boolean,
        default: false,
      },
      iconLarge: {
        type: Boolean,
        default: true,
      },
      variant: {
        type: String as () => 'default' | 'preview-leaflet',
        default: 'default',
      },
    },
    emits: ['close'],
    mixins: [mixin],
    methods: {
      close(): void {
        this.$emit('close');
      },
    },
  });
</script>

<style lang="postcss">
  #popup-container {
    @apply absolute top-0 w-full h-full z-[1000] flex flex-col backdrop-blur-[5px] bg-black/50;
    .mobile {
      @apply !max-w-[90%];
      min-height: var(--popup-min-h, 600px);
    }
    #popup-wrapper {
      @apply relative m-auto rounded-lg bg-white flex;
      max-height: calc(100% - 160px);
      max-width: calc(100% - 160px);

      &.flex {
        @media screen and (min-width: 1024px) {
          @apply w-[70%] flex-1;
        }
      }
      #close {
        @apply absolute cursor-pointer right-5 top-5 z-[300];
        @media (hover: hover) {
          &:hover {
            transition: transform 0.3s ease-out;
            transform: rotate(90deg);
          }
        }
        svg {
          @apply stroke-white stroke-[1.5px] h-[20px];
        }
      }
      #popup-header {
        @apply flex flex-col;
        #popup-icon {
          svg {
            @apply w-[50px] h-[50px] fill-grey-darkest opacity-10 stroke-white;
          }
          @apply mt-0 mx-auto mb-5;
          &.no-stroke {
            @apply stroke-white;
          }
          &.large {
            svg {
              @apply w-[110px] h-[110px];
            }
          }
        }
        #title {
          @apply text-center font-semibold text-[30px] text-primary mt-0 mb-5 mx-5 tracking-normal;
        }
      }
      #content {
        @apply flex-1 flex justify-center items-center;

        .description {
          @apply text-center font-light text-[21px] tracking-normal text-grey-middle p-[40px] max-w-[600px];
        }
      }
    }
    #footer {
      @apply flex justify-end items-end;
      &.centered-btn {
        @apply justify-center;
      }
      #close-btn {
        @apply mx-0 mt-0 mb-5;
      }
    }
  }
</style>
